import { useCallback, useEffect, useRef, useState } from "react";

import { useExternalNodeNames } from "./useExternalNodeNames";
import { validateComputeName } from "../utils";

const COMPUTE_NAME_COMPLETION_DELAY_MS = 250;

export const useComputeName = (activeOrgId: string | undefined) => {
  const [computeName, setComputeName] = useState("");
  const [submittedNameError, setSubmittedNameError] = useState("");
  const [hasCompletedStepOne, setHasCompletedStepOne] = useState(false);
  const [isStepOneNameSettled, setIsStepOneNameSettled] = useState(false);
  const completionTimerRef = useRef<number | null>(null);
  const [previousOrgId, setPreviousOrgId] = useState(activeOrgId);

  const {
    externalNodeNames,
    isError: isExternalNodeNamesError,
    isLoading: isExternalNodeNamesLoading,
  } = useExternalNodeNames();

  if (previousOrgId !== activeOrgId) {
    setPreviousOrgId(activeOrgId);
    setComputeName("");
    setSubmittedNameError("");
    setHasCompletedStepOne(false);
    setIsStepOneNameSettled(false);
  }

  const getComputeNameError = useCallback(
    (value: string) =>
      isExternalNodeNamesLoading
        ? ""
        : validateComputeName(
            value,
            isExternalNodeNamesError ? undefined : externalNodeNames,
          ) ||
          (isExternalNodeNamesError
            ? "Unable to verify compute name availability."
            : ""),
    [externalNodeNames, isExternalNodeNamesError, isExternalNodeNamesLoading],
  );

  useEffect(() => {
    return () => {
      if (completionTimerRef.current !== null) {
        window.clearTimeout(completionTimerRef.current);
      }
    };
  }, []);

  const handleComputeNameChange = useCallback(
    (value: string, completeImmediately = false) => {
      setComputeName(value);
      setIsStepOneNameSettled(false);
      setSubmittedNameError("");

      const validationError = getComputeNameError(value);
      setSubmittedNameError(value.trim() ? validationError : "");

      if (validationError) {
        if (completionTimerRef.current !== null) {
          window.clearTimeout(completionTimerRef.current);
          completionTimerRef.current = null;
        }

        return;
      }

      if (completionTimerRef.current !== null) {
        window.clearTimeout(completionTimerRef.current);
      }

      if (completeImmediately) {
        setHasCompletedStepOne(true);
        setIsStepOneNameSettled(true);
        completionTimerRef.current = null;

        return;
      }

      completionTimerRef.current = window.setTimeout(() => {
        setHasCompletedStepOne(true);
        setIsStepOneNameSettled(true);
        completionTimerRef.current = null;
      }, COMPUTE_NAME_COMPLETION_DELAY_MS);
    },
    [getComputeNameError],
  );

  const handleValidateName = useCallback(
    (value: string) => {
      setSubmittedNameError(
        value.trim() && !isExternalNodeNamesLoading
          ? getComputeNameError(value)
          : "",
      );
    },
    [getComputeNameError, isExternalNodeNamesLoading],
  );

  const nameError =
    submittedNameError ||
    (isExternalNodeNamesLoading || !computeName.trim()
      ? ""
      : getComputeNameError(computeName));

  const isReady =
    Boolean(activeOrgId) &&
    hasCompletedStepOne &&
    !isExternalNodeNamesLoading &&
    !isExternalNodeNamesError;
  const hasValidName = Boolean(
    computeName.trim() &&
    !nameError &&
    !isExternalNodeNamesLoading &&
    !isExternalNodeNamesError,
  );

  return {
    computeName,
    getComputeNameError,
    handleComputeNameChange,
    handleValidateName,
    hasValidName,
    isSettled: isStepOneNameSettled && isReady,
    isReady,
    nameError,
    setSubmitNameError: setSubmittedNameError,
  };
};
